/**
 * Terminal workspace tabs store — ONE WORKSPACE == ONE TAB.
 *
 * The set of open workspaces (and which one is active) is a purely client-side
 * concern: it is persisted to `localStorage`, namespaced by org/user when the
 * session exposes them (e.g. `ito.terminal.tabs.<orgId>.<userId>`), and never
 * written to Postgres. The workspace *content* lives server-side.
 *
 * The state lives in a tiny module-level external store so non-React callers
 * (command palette, hotkeys, the `+` picker) can drive it through
 * `openWorkspaceTab` / `closeWorkspaceTab` / `setActiveWorkspace` without a
 * React tree. `TerminalTabsProvider` merely subscribes to that store and
 * mirrors it into context for components.
 */

import {
  createContext,
  createElement,
  useContext,
  useEffect,
  useMemo,
  useSyncExternalStore,
  type ReactNode,
} from "react";

import { useSession } from "../../lib/session";

/** One open workspace tab. `id` is the workspace id. */
export interface TerminalTab {
  id: string;
  title: string;
  kind?: string;
  ephemeral?: boolean;
  dirty?: boolean;
}

export interface TerminalTabsState {
  tabs: TerminalTab[];
  activeId: string | null;
}

/** Optional metadata supplied when opening a workspace as a tab. */
export interface OpenWorkspaceTabMeta {
  title?: string;
  kind?: string;
  ephemeral?: boolean;
  dirty?: boolean;
}

export interface TerminalTabsContextValue extends TerminalTabsState {
  openTab: (workspaceId: string, meta?: OpenWorkspaceTabMeta) => void;
  closeTab: (workspaceId: string) => void;
  setActive: (workspaceId: string) => void;
  reorderTabs: (ids: string[]) => void;
}

const DEFAULT_STORAGE_KEY = "ito.terminal.tabs";

/**
 * The `localStorage` key for a session scope. Falls back to the bare key when
 * the session has no org/user (e.g. signed out or still loading).
 */
export function terminalTabsStorageKey(
  organizationId?: string | null,
  userId?: string | null,
): string {
  const scope = [organizationId, userId].filter((part): part is string =>
    Boolean(part),
  );
  return scope.length > 0
    ? `${DEFAULT_STORAGE_KEY}.${scope.join(".")}`
    : DEFAULT_STORAGE_KEY;
}

// --------------------------------------------------------------------------
// External store
// --------------------------------------------------------------------------

const EMPTY_STATE: TerminalTabsState = { tabs: [], activeId: null };

let state: TerminalTabsState = EMPTY_STATE;
let storageKey = DEFAULT_STORAGE_KEY;
let hydrated = false;
const listeners = new Set<() => void>();

function storage(): Storage | undefined {
  try {
    return globalThis.localStorage;
  } catch {
    // Accessing localStorage can throw in sandboxed/private contexts.
    return undefined;
  }
}

function isTab(value: unknown): value is TerminalTab {
  if (!value || typeof value !== "object") return false;
  const tab = value as Partial<TerminalTab>;
  return typeof tab.id === "string" && typeof tab.title === "string";
}

function parseStored(raw: string | null): TerminalTabsState {
  if (!raw) return EMPTY_STATE;
  try {
    const parsed = JSON.parse(raw) as Partial<TerminalTabsState>;
    const tabs = Array.isArray(parsed.tabs) ? parsed.tabs.filter(isTab) : [];
    const activeId =
      typeof parsed.activeId === "string" &&
      tabs.some((tab) => tab.id === parsed.activeId)
        ? parsed.activeId
        : (tabs[0]?.id ?? null);
    return { tabs, activeId };
  } catch {
    return EMPTY_STATE;
  }
}

function persist(): void {
  const store = storage();
  if (!store) return;
  try {
    store.setItem(storageKey, JSON.stringify(state));
  } catch {
    // Quota/serialization errors must never break the UI.
  }
}

function notify(): void {
  for (const listener of listeners) listener();
}

function commit(next: TerminalTabsState): void {
  state = next;
  persist();
  notify();
}

/**
 * Bind the store to an org/user scope, hydrating that scope's saved tabs.
 * Called by `TerminalTabsProvider` once the session is known; safe to call
 * repeatedly (no-op when the scope is unchanged).
 */
export function configureTerminalTabs(
  organizationId?: string | null,
  userId?: string | null,
): void {
  const nextKey = terminalTabsStorageKey(organizationId, userId);
  if (nextKey === storageKey && hydrated) return;
  storageKey = nextKey;
  hydrated = true;
  state = parseStored(storage()?.getItem(nextKey) ?? null);
  notify();
}

export function subscribeTerminalTabs(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Current tabs snapshot. Readable from non-React code. */
export function getTerminalTabsState(): TerminalTabsState {
  return state;
}

function mergedTab(
  existing: TerminalTab,
  workspaceId: string,
  meta?: OpenWorkspaceTabMeta,
): TerminalTab {
  return {
    ...existing,
    id: workspaceId,
    ...(meta?.title !== undefined ? { title: meta.title } : {}),
    ...(meta?.kind !== undefined ? { kind: meta.kind } : {}),
    ...(meta?.ephemeral !== undefined ? { ephemeral: meta.ephemeral } : {}),
    ...(meta?.dirty !== undefined ? { dirty: meta.dirty } : {}),
  };
}

/**
 * Open a workspace as a tab (or focus it when already open). Safe to call
 * from non-React code such as the command palette.
 */
export function openWorkspaceTab(
  workspaceId: string,
  meta?: OpenWorkspaceTabMeta,
): void {
  const existing = state.tabs.find((tab) => tab.id === workspaceId);
  if (existing) {
    const updated = mergedTab(existing, workspaceId, meta);
    if (state.activeId === workspaceId && sameTab(updated, existing)) return;
    commit({
      tabs: state.tabs.map((tab) => (tab.id === workspaceId ? updated : tab)),
      activeId: workspaceId,
    });
    return;
  }
  const tab: TerminalTab = {
    id: workspaceId,
    title: meta?.title ?? workspaceId,
    ...(meta?.kind !== undefined ? { kind: meta.kind } : {}),
    ...(meta?.ephemeral !== undefined ? { ephemeral: meta.ephemeral } : {}),
    ...(meta?.dirty !== undefined ? { dirty: meta.dirty } : {}),
  };
  commit({ tabs: [...state.tabs, tab], activeId: workspaceId });
}

function sameTab(a: TerminalTab, b: TerminalTab): boolean {
  return (
    a.id === b.id &&
    a.title === b.title &&
    a.kind === b.kind &&
    a.ephemeral === b.ephemeral &&
    a.dirty === b.dirty
  );
}

/**
 * Close a workspace tab. When the closed tab was active, the next tab (or the
 * previous one at the end) becomes active. Safe to call outside React.
 */
export function closeWorkspaceTab(workspaceId: string): void {
  const index = state.tabs.findIndex((tab) => tab.id === workspaceId);
  if (index === -1) return;
  const tabs = state.tabs.filter((tab) => tab.id !== workspaceId);
  const activeId =
    state.activeId === workspaceId
      ? (tabs[index]?.id ?? tabs[index - 1]?.id ?? null)
      : state.activeId;
  commit({ tabs, activeId });
}

/**
 * Activate an open workspace tab. Unknown ids are ignored so callers can't
 * point the terminal at a tab that isn't open. Safe to call outside React.
 */
export function setActiveWorkspace(workspaceId: string): void {
  if (!state.tabs.some((tab) => tab.id === workspaceId)) return;
  if (state.activeId === workspaceId) return;
  commit({ tabs: state.tabs, activeId: workspaceId });
}

/**
 * Reorder open tabs to the given id order. Unknown ids are ignored and any
 * tab missing from `ids` is appended, so a stale drag can never drop a tab.
 * Safe to call outside React.
 */
export function reorderWorkspaceTabs(ids: string[]): void {
  const byId = new Map(state.tabs.map((tab) => [tab.id, tab]));
  const tabs: TerminalTab[] = [];
  for (const id of ids) {
    const tab = byId.get(id);
    if (tab) {
      tabs.push(tab);
      byId.delete(id);
    }
  }
  for (const tab of byId.values()) tabs.push(tab);
  if (tabs.every((tab, index) => tab.id === state.tabs[index]?.id)) return;
  commit({ tabs, activeId: state.activeId });
}

// --------------------------------------------------------------------------
// React binding
// --------------------------------------------------------------------------

const TerminalTabsContext = createContext<TerminalTabsContextValue>({
  tabs: [],
  activeId: null,
  openTab: openWorkspaceTab,
  closeTab: closeWorkspaceTab,
  setActive: setActiveWorkspace,
  reorderTabs: reorderWorkspaceTabs,
});

/**
 * Provides the open-workspace tab set to the terminal shell. Scopes the
 * persisted tabs to the active organization/user as soon as the session
 * resolves.
 */
export function TerminalTabsProvider({ children }: { children: ReactNode }) {
  const session = useSession();
  const snapshot = useSyncExternalStore(
    subscribeTerminalTabs,
    getTerminalTabsState,
    getTerminalTabsState,
  );

  const organizationId = session.organizationId;
  const userId = session.user?.id ?? null;
  useEffect(() => {
    configureTerminalTabs(organizationId, userId);
  }, [organizationId, userId]);

  const value = useMemo<TerminalTabsContextValue>(
    () => ({
      tabs: snapshot.tabs,
      activeId: snapshot.activeId,
      openTab: openWorkspaceTab,
      closeTab: closeWorkspaceTab,
      setActive: setActiveWorkspace,
      reorderTabs: reorderWorkspaceTabs,
    }),
    [snapshot],
  );

  return createElement(TerminalTabsContext.Provider, { value }, children);
}

export function useTerminalTabs(): TerminalTabsContextValue {
  return useContext(TerminalTabsContext);
}

/**
 * Alias of `useTerminalTabs` for callers that name the concept after the
 * workspace it holds (the command palette's tab commands).
 */
export const useWorkspaceTabs = useTerminalTabs;
